/**
 * Customer-facing product subtitle (second line under the item title).
 *
 * Intended long-term authority for Link A/B documents (proposals, VOs, and
 * related customer surfaces) when those surfaces adopt a single second-line
 * pattern: meaningful description, never a Brand · Model echo of the title.
 *
 * Current adopters:
 * - Variation Order document (`VariationOrderDocument`)
 *
 * Not yet wired into proposal cards / PDF primary rows — those still use a
 * different composition (technical Brand Model subtitle + separate body copy).
 * Do not switch those surfaces here without an explicit product decision.
 */

function clean(value: string | null | undefined): string {
  return String(value ?? "").trim();
}

function normalizeForCompare(value: string): string {
  return clean(value)
    .toLowerCase()
    .replace(/[·•|/,_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function formatBrandModelDot(brand: string | null | undefined, model: string | null | undefined): string {
  return [clean(brand), clean(model)].filter(Boolean).join(" · ");
}

function formatBrandModelSpace(brand: string | null | undefined, model: string | null | undefined): string {
  return [clean(brand), clean(model)].filter(Boolean).join(" ");
}

function isDuplicateOfTitle(candidate: string, title: string): boolean {
  const a = normalizeForCompare(candidate);
  const b = normalizeForCompare(title);
  if (!a || !b) return false;
  return a === b;
}

function isBrandModelOnlySubtitle(
  candidate: string,
  brand: string | null | undefined,
  model: string | null | undefined,
): boolean {
  const normalized = normalizeForCompare(candidate);
  if (!normalized) return false;
  const dot = normalizeForCompare(formatBrandModelDot(brand, model));
  const space = normalizeForCompare(formatBrandModelSpace(brand, model));
  return Boolean((dot && normalized === dot) || (space && normalized === space));
}

export type CustomerFacingProductSubtitleInput = {
  name?: string | null;
  brand?: string | null;
  model?: string | null;
  /** Stored customer-facing description (e.g. polished / Cleo / manual). */
  description?: string | null;
};

/**
 * Resolve the second line under a customer-facing product title.
 * Authority: stored `description`. Omits the line when empty or when it would
 * only repeat Brand · Model / the title.
 */
export function resolveCustomerFacingProductSubtitle(
  input: CustomerFacingProductSubtitleInput,
): string | null {
  const title = clean(input.name);
  const description = clean(input.description);
  if (!description) return null;
  if (isDuplicateOfTitle(description, title)) return null;
  if (isBrandModelOnlySubtitle(description, input.brand, input.model)) return null;
  const brandModelSpace = formatBrandModelSpace(input.brand, input.model);
  const brandModelDot = formatBrandModelDot(input.brand, input.model);
  if (brandModelSpace && isDuplicateOfTitle(description, brandModelSpace)) return null;
  if (brandModelDot && isDuplicateOfTitle(description, brandModelDot)) return null;
  return description;
}

/** @deprecated Prefer `resolveCustomerFacingProductSubtitle` — VO alias for callers. */
export function resolveVoItemCustomerSubtitle(
  input: CustomerFacingProductSubtitleInput,
): string | null {
  return resolveCustomerFacingProductSubtitle(input);
}
